import { CommonModule } from '@angular/common';
import {
  Component,
  HostBinding,
  Input,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { BaseWidget } from '@app/widgets/base-widget/base-widget';
import { InputTypeEnum } from '@app/core/enums/input-type.enum';
import { VerificationTypeEnum } from '@app/core/enums/verification-type.enum';
import { AddressBook } from '@app/core/dto/address-book.dto';
import {
  BusinessInputNameConstant,
  BusinessRegistrationFormDto,
  BusinessRegistrationFunctionName,
  BusinessVerificationEnum,
  MessageType,
} from '@app/core/dto/business-registration-form.dto';
import { TimerService } from '@app/core/services/timer/timer.service';
import { ActionEnum } from '@app/core/dto/abstract-action.dto';
import { MediaUploaderComponent } from '@app/widgets/media-uploader/media-uploader.component';
import { MediaUploadTypes } from '@app/core/enums/media-type.enum';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { StringUtils } from '@app/core/utils/string.utils';
import { Observable } from 'rxjs';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import {
  CinVerificationDto,
  CompanyNameVerificationDto,
  FssaiVerificationDto,
  GSTINVerificaitonDto,
  MsmeVerificationDto,
  PanVerificationDto,
} from '@app/core/dto/entity-verification.dto';
import { PipeModule } from '@app/core/pipe/pipe.module';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormTypeEnum } from '@app/core/enums/form-type.enum';

@Component({
  selector: 'business-registration-dynamic-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatStepperModule,
    MediaUploaderComponent,
    PipeModule,
    MatTooltipModule,
  ],
  templateUrl: './business-registration-dynamic-form.component.html',
  styleUrls: ['./business-registration-dynamic-form.component.scss'],
})
export class BusinessRegistrationDynamicFormComponent
  extends BaseWidget
  implements OnInit
{
  @HostBinding('class') @Input() ngClass: any;
  @HostBinding('style') @Input() ngStyle: any;

  @Input() actionEvent: (event: any) => void;
  @Input() slot: any;

  @ViewChild('business_registration_stepper') stepper: MatStepper;
  @ViewChild(MediaUploaderComponent)
  mediaUploaderComponent!: MediaUploaderComponent;

  data: any;
  businessRegistrationForm: FormGroup;
  businessFormConfig: WritableSignal<any> = signal(null);
  inputTypeEnum = InputTypeEnum;
  formBottomData: any;
  gstListInfo: any;

  businessIdentificationVerified: WritableSignal<boolean> = signal(false);
  formDisabled: WritableSignal<boolean> = signal(true);
  verifyDisabled: WritableSignal<boolean> = signal(true);
  billingAddressObj: WritableSignal<Record<string, any>> = signal({});
  verificationInputValue: string;
  businessIdentification: VerificationTypeEnum;
  businessIdentificationValue: string;
  gstOptions: WritableSignal<Record<string, any>[]> = signal([]);
  showGSTList: WritableSignal<boolean> = signal(false);
  gstInByPan: WritableSignal<string> = signal(null);
  showBillingAddress: WritableSignal<boolean> = signal(false);
  billingAddress: string;
  addressBilling: AddressBook;
  customErrors: WritableSignal<Record<string, any>> = signal({});
  MessageType = MessageType;
  isButtonDisabled = false;
  isDisable: WritableSignal<Record<string, any>> = signal({});
  disableTill: number = 0;
  disableMessage: string = null;
  timerFor: string = null;
  validationRules: Record<string, any>[] = [];
  placeHolder: WritableSignal<Record<string, string>> = signal({});
  billingAddressLayoutData: Record<string, any>;
  mobileNoVerified: WritableSignal<boolean> = signal(false);
  isEmailExist: WritableSignal<boolean> = signal(null);
  handleBillingAddress: Record<string, any>[] = [];
  inputName = BusinessInputNameConstant;
  errorMessage: WritableSignal<Record<string, any>> = signal({});
  dataFromAction: WritableSignal<boolean> = signal(false);
  timer$: Observable<number> | undefined;
  businessFormFeilds: WritableSignal<Record<string, any>[]> = signal([]);
  verificationAction: Record<string, any>[] = [];
  showVerificationAction: WritableSignal<Record<string, any>> = signal({});
  StringUtils = StringUtils;
  BusinessRegistrationFunctionName = BusinessRegistrationFunctionName;
  isLinear: boolean = true;
  showLoader = signal<boolean>(false);
  hideMediaUploader: WritableSignal<boolean> = signal(false);
  VerificationTypeEnum = VerificationTypeEnum;
  selectedStepIndex: WritableSignal<number> = signal(0);
  disableSubmitButton: WritableSignal<boolean> = signal(false);
  updateDetails: boolean;
  handleInfoMessages: Record<string, any>[] = [];
  selectedBusinessIdentificationValue: WritableSignal<string> = signal(null);
  trackingAction: { [key: string]: any } = {};
  documentRequired: BusinessVerificationEnum[] = [
    BusinessVerificationEnum.FACTORY_LICENCE,
    BusinessVerificationEnum.LICENSE_REGISTRATION_CERTIFICATE_SHOPS_ACT,
    BusinessVerificationEnum.LOCAL_GOVT_PERMITS_HAWKERS_LICENSE,
    BusinessVerificationEnum.PARTNERSHIP_FIRM_AGREEMENT,
  ];

  constructor(
    private fb: FormBuilder,
    private timerService: TimerService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.data = this.slot.widget.data;
    this.data.payload = this.data?.formData?.payload;
    this.data.messages = this.data?.formData?.messages;
    this.businessFormConfig.set(
      this.data?.renderableComponents[0]?.businessForm?.stepper,
    );
    this.formBottomData =
      this.data?.renderableComponents[0]?.component?.bottomSection;
    this.gstListInfo = this.data?.renderableComponents[0]?.component?.gstInList;
    this.billingAddressLayoutData =
      this.data?.renderableComponents[0]?.component?.billingAddress;
    this.updateDetails = this.data?.formData?.isLoggedIn;

    this.getFormFields();
    this.createForm();
    this.addConditionalLogic();

    if (this.data?.payload) {
      this.dataFromAction.set(true);
      this.patchFormData(this.data?.payload);
      this.initializeTimers();
      this.showErrorOnLoad(this.data?.messages);
      this.goToNextStep();
    }

    this.subscribeToFirstStepValidation();
  }

  deleteDocumentFile() {
    if (this.mediaUploaderComponent) {
      this.mediaUploaderComponent.deleteFile();
    }
  }

  /**
   * get all fields and store in
   * businessFormFeilds
   */
  getFormFields() {
    this.businessFormConfig().forEach((stepper) => {
      stepper?.fields.forEach((field) => {
        if (!StringUtils.isEmpty(field)) {
          this.businessFormFeilds().push(field);
        }
      });
    });
  }

  createForm(): void {
    const formGroupConfig = {};
    this.businessFormFeilds().forEach((field) => {
      const validators = [];

      // add validation rules for options
      if (field?.name === BusinessInputNameConstant.BUSINESS_IDENTIFICATION) {
        field?.options.forEach((option) => {
          this.validationRules.push({ [option?.value]: option?.validation });
          this.handleBillingAddress.push({
            [option?.value]: option?.billingAddress,
          });
          this.verificationAction.push({
            [option?.value]: option?.action,
          });

          if (option?.action?.action?.tracking) {
            this.trackingAction[option?.value] =
              option?.action?.action?.tracking;
          }

          if (option?.showInfoMessage) {
            this.handleInfoMessages.push({
              [option?.value]: option?.showInfoMessage,
            });
          }
        });
      }

      if (field?.required) {
        validators.push(Validators.required);
      }
      if (field?.minlength) {
        validators.push(Validators.minLength(field?.minlength));
      }
      if (field?.pattern) {
        validators.push(Validators.pattern(field?.pattern));
      }
      if (field?.customValidationFunction) {
        const validationFn = this[field.customValidationFunction]?.();
        if (validationFn) {
          validators.push(validationFn);
        }
      }

      this.placeHolder.set({
        ...this.placeHolder(),
        [field?.name]: field?.placeholder,
      });

      formGroupConfig[field?.name] = [field?.defaultValue || '', validators];
    });

    this.businessRegistrationForm = this.fb.group(formGroupConfig);
  }

  /**
   * The function `getIndividualField` retrieves a specific field from a list based on the field name
   * provided.
   * @param {BusinessInputNameConstant} fieldName - fieldName is a parameter of type
   * BusinessInputNameConstant that is used in the getIndividualField function to find and return a
   * specific item from the businessFormFields array based on the provided fieldName.
   * @returns The `getIndividualField` function is returning the item from the `businessFormFields` array
   * that has a `name` property matching the `fieldName` parameter.
   */
  getIndividualField(fieldName: BusinessInputNameConstant) {
    return this.businessFormFeilds().find((item) => item.name === fieldName);
  }

  // Evaluate condition based on the operator
  evaluateCondition(
    operator: string | undefined,
    fieldValue: any,
    condition: any,
  ): boolean {
    if (condition?.value && Array.isArray(condition?.value)) {
      // Handle arrays: compare the field value against each item in the array
      switch (operator) {
        case 'equals':
          return condition.value.includes(fieldValue);
        case 'notEquals':
          return !condition.value.includes(fieldValue);
        default:
          return false;
      }
    }
    return;
  }

  /**
   * this function responsible for show and hide
   * the input field according to the condition.
   *
   * @param field
   * @returns boolean
   */
  isFieldEnabled(field: any): boolean {
    if (!field.condition) return true;

    const relatedFieldValue = this.getInputFieldValue(field?.condition?.field);

    if (field?.condition?.disableValues) {
      field.isDisable =
        field?.condition?.disableValues.includes(relatedFieldValue);
    } else {
      field.isDisable = false;
    }

    return this.evaluateCondition(
      field.condition.operator,
      relatedFieldValue,
      field.condition,
    );
  }

  // get validation for bussiness Identification options
  getValidatorsForOption(option: string) {
    this.setVerificationActionButton();
    this.businessIdentification = VerificationTypeEnum[option];
    const field: Record<string, any> = this.verificationIdentificationField;
    const validators: ValidatorFn[] = this.setValidationForVerificationOption(
      option,
      field,
    );

    // Apply the validators to the form control
    const control: AbstractControl<any, any> = this.verifyIdentification;
    if (control) {
      control.setValidators(validators);
      control.updateValueAndValidity();
    }
  }

  getFormData() {
    return this.businessRegistrationForm.value;
  }

  getInputFieldValue(fieldName: string) {
    return this.businessRegistrationForm.get(fieldName).value;
  }

  /* 
    set data to the form when request received 
    from the backend.
  */
  patchFormData(payload: any): void {
    if (
      typeof payload === 'object' &&
      payload &&
      Object.keys(payload).length > 0
    ) {
      const addressInfo: AddressBook = payload?.addressBook;
      this.businessRegistrationForm.patchValue({
        [BusinessInputNameConstant.FULL_NAME]: payload?.fullName,
        [BusinessInputNameConstant.EMAIL_ID]: payload?.emailId,
        [BusinessInputNameConstant.MOBILE_NO]: payload?.mobileNo,
        [BusinessInputNameConstant.OTP]: payload?.otp,
        [BusinessInputNameConstant.BUSINESS_IDENTIFICATION]:
          payload?.businessIdentification ?? '',
        [BusinessInputNameConstant.VERIFY_IDENTIFICATION]:
          payload?.verifyIdentification,
        [BusinessInputNameConstant.COMPANY_NAME]: payload?.companyName,
        [BusinessInputNameConstant.GSTIN]: payload?.gstin,
        [BusinessInputNameConstant.BUSINESS_DOCUMENT]: payload?.businessDocument
          ? [
              ...this.businessRegistrationForm.value?.[
                BusinessInputNameConstant.BUSINESS_DOCUMENT
              ],
              ...payload?.businessDocument,
            ]
          : null,
      });

      if (this.documentRequired.includes(payload?.businessIdentification)) {
        this.disableSubmitButton.set(
          StringUtils.isEmpty(this.businessDocument.value),
        );
      }

      this.isEmailExist.set(payload?.isEmailExist);
      this.businessIdentification = payload?.businessIdentification;
      this.mobileNoVerified.set(payload.mobileNoVerified);
      this.businessIdentificationVerified.set(
        payload?.businessIdentificationVerified,
      );
      this.gstInByPan.set(payload?.gstin);
      if (addressInfo?.address) {
        this.addressBilling = new AddressBook({
          ...addressInfo,
        });

        this.billingAddress = `${addressInfo?.address} ${addressInfo?.address2 ?? ''} ${addressInfo?.city} ${addressInfo?.state} ${addressInfo?.landmark ?? ''} ${addressInfo?.pincode}`;
      }

      if (payload?.businessIdentificationVerified) {
        const verificationActionButton = this.verificationAction.find(
          (item) => payload?.businessIdentification in item,
        );
        this.showVerificationAction.set(
          verificationActionButton?.[payload?.businessIdentification],
        );
        this.triggerEntityVerificationActionOnLoad();

        if (payload?.gstin) {
          this.verifyGstNo();
        }
      } else {
        Object.keys(payload).forEach((key) => {
          const field = this.getIndividualField(key); // Custom method to find field details by name
          if (field?.actions?.event?.onChange?.functionName) {
            this.handleEvent(
              payload[key],
              field,
              field?.actions?.event?.onChange?.functionName,
              field?.actions?.event?.onChange?.params,
            );
          }
        });
      }

      this.isFirstStepIsValid();
    }
  }

  /**
   * addConditionalLogic set the field required for
   * company and document field
   */
  addConditionalLogic(): void {
    // Listen for changes in businessIdentification
    this.businessRegistrationForm
      .get(BusinessInputNameConstant.BUSINESS_IDENTIFICATION)
      ?.valueChanges.subscribe((value) => {
        const companyNameItem = this.getIndividualField(
          BusinessInputNameConstant.COMPANY_NAME,
        );
        const businessDocumentItem = this.getIndividualField(
          BusinessInputNameConstant.BUSINESS_DOCUMENT,
        );
        if (!StringUtils.isEmpty(value)) {
          if (this.documentRequired.includes(value)) {
            this.businessDocument?.setValidators([Validators.required]);
            businessDocumentItem.required = true;
          } else {
            businessDocumentItem.required = false;
            this.businessDocument?.clearValidators();
          }

          if (value !== BusinessVerificationEnum.OTHER) {
            this.companyName?.setValidators([Validators.required]);
            companyNameItem.required = true;
          } else {
            companyNameItem.required = false;
            this.companyName?.clearValidators(); // Remove required validation if not 'Other'
          }
          this.companyName?.updateValueAndValidity();
          this.businessDocument?.updateValueAndValidity(); // Update validation status
        }
      });
  }

  createSimulatedEvent(fieldName: string, value: any): any {
    return {
      target: {
        name: fieldName,
        value: value,
      },
      value: value,
    };
  }

  initializeTimers(): void {
    this.businessFormFeilds().forEach((field) => {
      // Check if the rightCTA object has a timer and initialize it
      const rightCTATimer: { duration: number; message: string } =
        field?.actions?.leftRightCTA?.rightCTA?.timer;

      if (rightCTATimer) {
        this.timerService.resetTimer(field.name, rightCTATimer.duration);
        this.subscribeToTimer(field);
      }
    });
  }

  subscribeToTimer(field: Record<string, any>): void {
    const fieldName: string = field.name;
    const timer$ = this.timerService.getTimerObservable(fieldName);
    // Subscribe to the timer observable and update the signal
    timer$?.subscribe((remaining) => {
      this.updateCTAButtonState(fieldName, remaining); // Call method to update the button state
    });
  }

  updateCTAButtonState(fieldName: string, remaining: number): void {
    this.isDisable.update((data) => ({
      ...data,
      [fieldName]: {
        rightCTA: remaining > 0,
      },
    }));
  }

  getTimerObservable(field: Record<string, any>) {
    const fieldName: string = field?.name;
    return this.timerService.getTimerObservable(fieldName);
  }

  getTimerMessage(fieldName: string): string {
    const field: Record<string, any> = this.businessFormFeilds().find(
      (f) => f.name === fieldName,
    );
    return field?.actions?.leftRightCTA?.rightCTA?.timer?.message || '';
  }

  /**
   * getBizRegFromData is responsible for get the
   * input field value and businessIdentification
   * @returns BusinessRegistrationFormDto
   */
  protected async _getBizRegFromData(): Promise<any> {
    this.businessRegistrationForm.markAllAsTouched();

    const businessIdentification: string = this.getInputFieldValue(
      BusinessInputNameConstant.BUSINESS_IDENTIFICATION,
    );
    let gstin: string;
    if (businessIdentification === VerificationTypeEnum.GST) {
      gstin = this.verifyIdentification.value;
    } else if (businessIdentification === VerificationTypeEnum.COMPANY_NAME) {
      gstin = null;
    } else {
      gstin = this.getInputFieldValue(BusinessInputNameConstant.GSTIN);
    }

    return {
      [BusinessInputNameConstant.FULL_NAME]: this.getInputFieldValue(
        BusinessInputNameConstant.FULL_NAME,
      ),
      [BusinessInputNameConstant.EMAIL_ID]: this.getInputFieldValue(
        BusinessInputNameConstant.EMAIL_ID,
      ),
      [BusinessInputNameConstant.MOBILE_NO]: this.getInputFieldValue(
        BusinessInputNameConstant.MOBILE_NO,
      ),
      businessIdentification,
      [BusinessInputNameConstant.VERIFY_IDENTIFICATION]:
        this.verifyIdentification?.value,
      [BusinessInputNameConstant.COMPANY_NAME]: this.companyName.value,
      // [BusinessInputNameConstant.IB_CREDIT]: this.getInputFieldValue(
      //   BusinessInputNameConstant.IB_CREDIT,
      // ),
      [BusinessInputNameConstant.BUSINESS_DOCUMENT]: this.getInputFieldValue(
        BusinessInputNameConstant.BUSINESS_DOCUMENT,
      ),
      gstin,
      mobileNoVerified: this.mobileNoVerified(),
      businessIdentificationVerified: this.businessIdentificationVerified(),
      billingAddress: {
        name: this.billingAddressObj()?.name,
        address: this.billingAddressObj()?.address,
        address2: this.billingAddressObj()?.address2,
        state: this.billingAddressObj()?.state,
        city: this.billingAddressObj()?.city,
        landMark: this.billingAddressObj()?.landMark,
        pincode: this.billingAddressObj()?.pincode,
        phone: this.billingAddressObj()?.phone,
      },
      addressBook: {
        ...this.addressBilling,
      },
      isEmailExist: this.isEmailExist(),
    };
  }

  onUpload(event: any) {
    const fileUrl = this.getInputFieldValue(
      BusinessInputNameConstant.BUSINESS_DOCUMENT,
    );
    if (fileUrl?.[0]?.url) {
      this.deleteFile(fileUrl?.[0]?.url);
    }
    if (event.type === MediaUploadTypes.IMAGE) {
      this.businessRegistrationForm.patchValue({
        [BusinessInputNameConstant.BUSINESS_DOCUMENT]: [event],
      });
    }
    this.hideMediaUploader.set(false);
  }

  deleteFile(fileUrl: string) {
    this.apiService
      .callFileDeleteApi({
        url: fileUrl,
      })
      .subscribe({
        next: (res) => {},
        error: () => {},
      });
  }

  async editBillingAddress(actionType) {
    this.onActionCall(actionType);
  }

  /**
   * on change of business identification this function is invoke
   * @param event
   */
  async getBusinessIndentificaiton(event) {
    const value: string = event?.value ? event?.value : event;

    this.selectedBusinessIdentificationValue.set(value);

    this.getValidatorsForOption(value);

    const item: Record<string, any> = this.getIndividualField(
      BusinessInputNameConstant.BUSINESS_IDENTIFICATION,
    );
    const billingAddress = item.options.find((item) => item.value === value);

    this.businessIdentificationValue = billingAddress?.label;

    this.resetBusinessFields(this.verificationIdentificationField);

    this.dataFromAction.set(false);

    if (billingAddress) {
      const billingLayout = this.getIndividualField(
        BusinessInputNameConstant.BILLING_ADDRESS_LAYOUT,
      );
      this.showBillingAddress.set(billingAddress?.billingAddress?.show);
      billingLayout.required = billingAddress?.billingAddress?.required;
    }

    this.deleteErrorMessage(BusinessInputNameConstant.VERIFY_IDENTIFICATION);

    this.businessRegistrationForm.patchValue({
      [BusinessInputNameConstant.VERIFY_IDENTIFICATION]: null,
    });
    this.markInputTouchState(
      BusinessInputNameConstant.VERIFY_IDENTIFICATION,
      false,
    );
    this.placeHolder.set({
      ...this.placeHolder(),
      [BusinessInputNameConstant.VERIFY_IDENTIFICATION]: StringUtils.format(
        'Enter {0}',
        this.businessIdentificationValue,
      ),
    });
  }

  isFieldHidden(fieldName: string): boolean {
    const field = this.getIndividualField(fieldName);
    const value = this.getInputFieldValue(fieldName);

    if (fieldName === BusinessInputNameConstant.COMPANY_NAME) {
      return field?.isDisable && !value;
    }
    return false;
  }

  resetBusinessFields(field) {
    // avoid reset values during slot reloading due to action
    if (this.dataFromAction()) {
      this.dataFromAction.set(false);
      return;
    }

    let fileUrl = this.getInputFieldValue(
      BusinessInputNameConstant.BUSINESS_DOCUMENT,
    );
    if (fileUrl?.[0]?.url) {
      this.deleteFile(fileUrl?.[0]?.url);
      this.hideMediaUploader.set(true);
    }
    this.deleteDocumentFile();
    this.businessIdentificationVerified.set(false);
    this.showBillingAddress.set(false);
    this.businessRegistrationForm.patchValue({
      [BusinessInputNameConstant.COMPANY_NAME]: null,
      [BusinessInputNameConstant.GSTIN]: null,
      [BusinessInputNameConstant.BUSINESS_DOCUMENT]: null,
    });

    // reset address data
    this.addressBilling = null;
    if (this.data) {
      this.data.addressBook = null;
      this.data.billingAddress = null;
      if (!StringUtils.isEmpty(this.data?.payload)) {
        this.data.payload.addressBook = null;
        this.data.payload.billingAddress = null;
      }
    }

    this.verificationIdentificationField.isVerified = false;
    this.updateFormField(this.verificationIdentificationField);

    this.markInputTouchState(BusinessInputNameConstant.COMPANY_NAME, false);

    this.showGSTList.set(false);
    this.showLoader.set(false);
    this.billingAddressObj.set(null);
    this.gstInByPan.set(null);
    this.companyName.enable();
    this.billingAddress = null;
  }

  getGstInByPanValue(gstIn: string) {
    if (gstIn === this.gstInByPan()) {
      return;
    }
    if (!StringUtils.isEmpty(gstIn)) {
      this.setCustomError(BusinessInputNameConstant.GSTIN, false);
      this.deleteErrorMessage(BusinessInputNameConstant.GSTIN);
    }

    this.showBillingAddress.set(false);
    this.billingAddressObj.set(null);
    this.businessRegistrationForm.patchValue({
      [BusinessInputNameConstant.COMPANY_NAME]: null,
    });
    this.markInputTouchState(BusinessInputNameConstant.COMPANY_NAME, false);
    if (this.businessIdentification === VerificationTypeEnum.COMPANY_NAME) {
      this.showVerificationAction.set(null);
      this.businessRegistrationForm.patchValue({
        [BusinessInputNameConstant.COMPANY_NAME]: gstIn,
      });
    } else {
      this.showVerificationAction.set({
        text: this.gstListInfo?.button?.text,
        action: {
          triggerExtraStandardEvents:
            BusinessRegistrationFunctionName.VERIFY_GST_NO,
        },
      });
    }

    this.gstInByPan.set(gstIn);
    this.disableSubmitButton.set(false);
  }

  /**
   * confirmPageExit is responsible for prevent
   * the page reload
   * @param event
   */
  confirmPageExit(event: BeforeUnloadEvent) {
    event.preventDefault();
  }

  get companyName() {
    return this.businessRegistrationForm.get(
      BusinessInputNameConstant.COMPANY_NAME,
    );
  }

  get verifyIdentification() {
    return this.businessRegistrationForm.get(
      BusinessInputNameConstant.VERIFY_IDENTIFICATION,
    );
  }

  get businessDocument() {
    return this.businessRegistrationForm.get(
      BusinessInputNameConstant.BUSINESS_DOCUMENT,
    );
  }

  get verificationIdentificationField() {
    return this.getIndividualField(
      BusinessInputNameConstant.VERIFY_IDENTIFICATION,
    );
  }

  updateFormField(updatedField) {
    const businessFormFeilds: Record<string, any>[] = this.businessFormFeilds();
    const fieldIndex = businessFormFeilds.findIndex(
      (field) => field.name === updatedField.name,
    );

    if (fieldIndex !== -1) {
      // Create a new array with the updated field
      const updatedConfig = [
        ...businessFormFeilds.slice(0, fieldIndex),
        updatedField,
        ...businessFormFeilds.slice(fieldIndex + 1),
      ];

      // Set the updated array to the signal
      this.businessFormFeilds.set(updatedConfig);
    }
  }

  async verifyGstNo() {
    this.formDisabled.set(true);
    if (StringUtils.isEmpty(this.gstInByPan())) {
      this.setCustomError(BusinessInputNameConstant.GSTIN, true);
      this.errorMessage.set({
        ...this.errorMessage(),
        [BusinessInputNameConstant.GSTIN]: {
          text: 'Please select GSTIN.',
          type: MessageType.ERROR,
        },
      });
      this.markInputTouchState(BusinessInputNameConstant.GSTIN, false);

      return;
    }

    if (!StringUtils.isEmpty(this.gstInByPan())) {
      await this.fetchGstDetailsByGstNo(
        new GSTINVerificaitonDto(VerificationTypeEnum.GST, this.gstInByPan()),
      );
    }
  }

  handleEvent(
    event: any,
    field: any,
    actionName: string,
    params: Record<string, any>,
  ): void {
    if (actionName && typeof this[actionName] === 'function') {
      this[actionName](event, field, params);
    }
  }

  validateVerificationInput(event: Event, field, params) {
    const currentValue: string = (event.target as HTMLSelectElement).value;
    if (
      this.verificationInputValue &&
      this.verificationInputValue !== currentValue
    ) {
      this.resetBusinessFields(field);
    }
  }

  async verifyBusinessIdentification(event, field, param) {
    const businessRegistrationData: Partial<BusinessRegistrationFormDto> =
      this.getFormData();
    const verificationType: VerificationTypeEnum =
      VerificationTypeEnum[
        businessRegistrationData?.[
          BusinessInputNameConstant.BUSINESS_IDENTIFICATION
        ]
      ];
    const verificationValue: string =
      businessRegistrationData?.[
        BusinessInputNameConstant.VERIFY_IDENTIFICATION
      ];
    this.verificationInputValue = verificationValue;

    if (StringUtils.isEmpty(verificationValue)) {
      this.setCustomError(
        BusinessInputNameConstant.VERIFY_IDENTIFICATION,
        true,
      );
      this.markInputTouchState(
        BusinessInputNameConstant.VERIFY_IDENTIFICATION,
        true,
      );
      return;
    }

    if (this.isInputInvalid(BusinessInputNameConstant.VERIFY_IDENTIFICATION)) {
      return;
    }

    if (verificationType === VerificationTypeEnum.GST) {
      await this.fetchGstDetailsByGstNo(
        new GSTINVerificaitonDto(verificationType, verificationValue),
        field,
      );
    }

    if (verificationType === VerificationTypeEnum.PAN) {
      await this.fetchGstListByPanOrCompanyName(
        new PanVerificationDto(verificationType, verificationValue),
        field,
      );
    }

    if (verificationType === VerificationTypeEnum.COMPANY_NAME) {
      await this.fetchGstListByPanOrCompanyName(
        new CompanyNameVerificationDto(verificationType, verificationValue),
        field,
      );
    }

    if (verificationType === VerificationTypeEnum.CIN) {
      this.fetchEntityDetails(
        new CinVerificationDto(verificationType, verificationValue),
        field,
      );
    }

    if (verificationType === VerificationTypeEnum.MSME) {
      this.fetchEntityDetails(
        new MsmeVerificationDto(verificationType, verificationValue),
        field,
      );
    }

    if (verificationType === VerificationTypeEnum.FSSAI) {
      this.fetchEntityDetails(
        new FssaiVerificationDto(verificationType, verificationValue),
        field,
      );
    }
  }

  verifyEmail(event: Event, field, params: Record<string, any>) {
    const emailId: string =
      (event?.target as HTMLSelectElement)?.value ?? params?.email;
    if (this.data?.emailId != emailId && StringUtils.isValidEmail(emailId)) {
      this.onActionCall(params?.actionType);
    }
  }

  /**
   * billingAddress is responsible for render the billing address form
   *
   * @param actionType
   */
  async onActionCall(actionType, index?: number) {
    if (
      [
        ActionEnum.VERIFY_USER,
        ActionEnum.VERIFY_OTP,
        ActionEnum.RESEND_OTP,
      ].includes(actionType)
    ) {
      if (this.isInputInvalid(BusinessInputNameConstant.MOBILE_NO)) {
        return;
      }

      if (
        actionType === ActionEnum.VERIFY_OTP &&
        this.isInputInvalid(BusinessInputNameConstant.OTP)
      ) {
        return;
      }
    }

    const businessRegistrationData: Partial<BusinessRegistrationFormDto> =
      this.getFormData();
    const additionalFormData = await this._getBizRegFromData();

    const action = {
      actionData: {
        type: actionType,
        params: {
          ...businessRegistrationData,
          ...additionalFormData,
        },
      },
    };

    this.actionEvent(action);
  }

  // Business Identificaiton API calls

  private async fetchGstDetailsByGstNo(gstDto: GSTINVerificaitonDto, field?) {
    this.showLoader.set(true);
    return this.apiService
      .callVerificationApi({
        type: gstDto?.type,
        gstNumber: gstDto?.gstNumber,
        payload: {
          formType: FormTypeEnum.BUSINESS_REGISTRATION_FORM,
          formData: this.businessRegistrationForm.value,
        },
      })
      .subscribe({
        next: async (res) => {
          this.showLoader.set(false);
          const response = res.RESPONSE?.data;

          if (!response.status) {
            if (response?.apiError) {
              const companyNameItem = this.getIndividualField(
                BusinessInputNameConstant.COMPANY_NAME,
              );

              companyNameItem.required = false;
              this.companyName?.clearValidators();
              this.companyName?.updateValueAndValidity();

              await this.submitBusinessRegistrationForm(null, null, {
                apiError: response?.apiError,
              });
              return;
            }

            const inputName: string = this.gstInByPan()
              ? BusinessInputNameConstant.GSTIN
              : BusinessInputNameConstant.VERIFY_IDENTIFICATION;
            this.setCustomError(inputName, true);
            this.errorMessage.set({
              ...this.errorMessage(),
              [inputName]: {
                text: response.message,
                type: MessageType.ERROR,
              },
            });

            this.resetBusinessFields(field);
          }

          if (response.status) {
            this.businessRegistrationForm
              .get(BusinessInputNameConstant.GSTIN)
              ?.setValue(this.gstInByPan());
            this.deleteErrorMessage(
              BusinessInputNameConstant.VERIFY_IDENTIFICATION,
            );
            this.deleteErrorMessage(BusinessInputNameConstant.GSTIN);
            this.setCustomError(
              BusinessInputNameConstant.VERIFY_IDENTIFICATION,
              false,
            );
            this.setCustomError(BusinessInputNameConstant.GSTIN, false);

            this.businessIdentificationVerified.set(true);
            this.businessRegistrationForm.patchValue({
              [BusinessInputNameConstant.COMPANY_NAME]: response?.businessName,
            });

            this.billingAddressObj.set({
              state: response?.state?.name,
              city: response?.city?.name,
              pincode: response?.pincode,
              address: response?.address,
              name: response?.name ?? this.data?.payload?.fullName,
              phone: this.data?.payload?.mobileNo,
            });
            this.companyName.disable();

            this.showBillingAddress.set(true);
            this.showVerificationAction.set(null);

            if (this.trackingAction?.GST && gstDto?.gstNumber) {
              const trackingActionReq = {
                tracking: this.trackingAction.GST,
              };
              const triggerTracking: boolean = super.getTrackingData(
                trackingActionReq,
                gstDto?.gstNumber,
              );
              if (triggerTracking) {
                this.actionEvent({
                  actionData: trackingActionReq,
                });
              }
            }
          }
        },
        error: (err) => {
          this.setCustomError(
            BusinessInputNameConstant.VERIFY_IDENTIFICATION,
            true,
          );
          this.errorMessage.set({
            ...this.errorMessage(),
            [BusinessInputNameConstant.VERIFY_IDENTIFICATION]: {
              text: 'Gst details not founds',
              type: MessageType.ERROR,
            },
          });
          this.resetBusinessFields(field);
        },
      });
  }

  private async fetchGstListByPanOrCompanyName(
    params: Record<string, any>,
    field?: Record<string, any>,
  ) {
    this.showLoader.set(true);
    return this.apiService.callVerificationApi(params).subscribe({
      next: async (res) => {
        const response = res.RESPONSE?.data;

        if (!response.status) {
          if (response?.apiError) {
            // List of fields to process
            const fieldsToClearValidators = [
              BusinessInputNameConstant.COMPANY_NAME,
              BusinessInputNameConstant.GSTIN,
            ];

            fieldsToClearValidators.forEach((fieldName) => {
              // Get the individual field and clear validators
              const fieldItem = this.getIndividualField(fieldName);
              const fieldControl = this.businessRegistrationForm.get(fieldName);

              if (fieldItem) {
                fieldItem.required = false;
              }

              fieldControl?.clearValidators();
              fieldControl?.updateValueAndValidity();
            });

            await this.submitBusinessRegistrationForm(null, null, {
              apiError: response?.apiError,
            });
            return;
          }

          this.setCustomError(
            BusinessInputNameConstant.VERIFY_IDENTIFICATION,
            true,
          );
          this.errorMessage.set({
            ...this.errorMessage(),
            [BusinessInputNameConstant.VERIFY_IDENTIFICATION]: {
              text: response.message,
              type: MessageType.ERROR,
            },
          });
          this.gstOptions.set([]);
          this.resetBusinessFields(field);
        } else {
          this.setCustomError(
            BusinessInputNameConstant.VERIFY_IDENTIFICATION,
            false,
          );
          this.showLoader.set(false);
          this.showGSTList.set(true);

          this.gstOptions.set(
            response?.gstList.map((item) => ({
              value:
                params.type === VerificationTypeEnum.COMPANY_NAME
                  ? item.legalBusinessName
                  : item.gstin,
            })),
          );

          this.verificationIdentificationField.isVerified = true;
          this.updateFormField(this.verificationIdentificationField);
          this.deleteErrorMessage(
            BusinessInputNameConstant.VERIFY_IDENTIFICATION,
          );

          this.businessIdentificationVerified.set(true);

          if (params.type !== VerificationTypeEnum.COMPANY_NAME) {
            this.businessRegistrationForm
              .get(BusinessInputNameConstant.GSTIN)
              ?.setValue(this.gstInByPan());
            this.showVerificationAction.set({
              text: this.gstListInfo?.button?.text,
              action: {
                triggerExtraStandardEvents: 'verifyGstNo',
              },
            });

            if (this.showBillingAddress()) {
              this.verifyGstNo();
            }
          } else {
            if (
              StringUtils.isEmpty(
                this.getInputFieldValue(BusinessInputNameConstant.COMPANY_NAME),
              )
            ) {
              this.businessRegistrationForm
                .get(BusinessInputNameConstant.COMPANY_NAME)
                ?.setValue(
                  this.getInputFieldValue(
                    BusinessInputNameConstant.VERIFY_IDENTIFICATION,
                  ),
                );
            }
            this.businessRegistrationForm
              .get(BusinessInputNameConstant.GSTIN)
              ?.setValue(
                this.getInputFieldValue(BusinessInputNameConstant.COMPANY_NAME),
              );
            this.showVerificationAction.set(null);
          }
        }
      },
      error: (err) => {
        this.setCustomError(
          BusinessInputNameConstant.VERIFY_IDENTIFICATION,
          true,
        );
        this.errorMessage.set({
          ...this.errorMessage(),
          [BusinessInputNameConstant.VERIFY_IDENTIFICATION]: {
            text:
              err?.error?.result?.response?.message[0] ??
              StringUtils.format(
                '{0} details not found.',
                params?.companyName || params?.panNumber,
              ),
            type: MessageType.ERROR,
          },
        });
        this.resetBusinessFields(field);
      },
    });
  }

  private async fetchEntityDetails(
    params: Record<string, any>,
    field?: Record<string, any>,
  ) {
    this.showLoader.set(true);
    return this.apiService.callVerificationApi(params).subscribe({
      next: async (res) => {
        this.showLoader.set(false);
        const response = res.RESPONSE?.data;

        if (!response.status) {
          if (response?.apiError) {
            // List of fields to process
            const fieldsToClearValidators = [
              BusinessInputNameConstant.COMPANY_NAME,
              BusinessInputNameConstant.GSTIN,
            ];

            fieldsToClearValidators.forEach((fieldName) => {
              // Get the individual field and clear validators
              const fieldItem = this.getIndividualField(fieldName);
              const fieldControl = this.businessRegistrationForm.get(fieldName);

              if (fieldItem) {
                fieldItem.required = false;
              }

              fieldControl?.clearValidators();
              fieldControl?.updateValueAndValidity();
            });

            await this.submitBusinessRegistrationForm(null, null, {
              apiError: response?.apiError,
            });
            return;
          }

          this.setCustomError(
            BusinessInputNameConstant.VERIFY_IDENTIFICATION,
            true,
          );
          this.gstOptions.set([]);
          this.errorMessage.set({
            ...this.errorMessage(),
            [BusinessInputNameConstant.VERIFY_IDENTIFICATION]: {
              text: response.message,
              type: MessageType.ERROR,
            },
          });

          this.resetBusinessFields(field);
        } else {
          this.setCustomError(
            BusinessInputNameConstant.VERIFY_IDENTIFICATION,
            false,
          );
          this.deleteErrorMessage(
            BusinessInputNameConstant.VERIFY_IDENTIFICATION,
          );

          this.businessIdentificationVerified.set(true);

          this.businessRegistrationForm.patchValue({
            companyName: response?.businessName,
          });

          this.billingAddressObj.set({
            state: response?.state?.name,
            city: response?.city?.name,
            pincode: response?.pincode,
            address: response?.address,
            name: response?.name ?? this.data?.payload?.fullName,
            phone: this.data?.payload?.mobileNo,
          });
          this.companyName.disable();
          this.showVerificationAction.set(null);
          this.showBillingAddress.set(true);
        }
      },
      error: () => {
        this.errorMessage.set({
          [BusinessInputNameConstant.VERIFY_IDENTIFICATION]: {
            text: `Details not founds`,
            type: MessageType.ERROR,
          },
        });
        this.markInputTouchState(
          BusinessInputNameConstant.VERIFY_IDENTIFICATION,
          false,
        );
        this.resetBusinessFields(field);
      },
    });
  }

  updateStepIndex(index) {
    this.selectedStepIndex.set(index);
  }
  // submit registration form
  async submitBusinessRegistrationForm(
    event: Event,
    field,
    param?: Record<string, any>,
  ) {
    this.isFirstStepIsValid();
    this.goToNextStep();

    if (!(await this._validateForm())) {
      const additionalFormData: Partial<BusinessRegistrationFormDto> =
        await this._getBizRegFromData();

      const action = {
        actionData: {
          type: ActionEnum.BUSINESS_REGISTRATION,
          params: {
            ...additionalFormData,
            ...param,
          },
        },
      };

      this.actionEvent(action);
    }
  }

  private async _validateForm() {
    const fieldsWithErrors: string[] = [];

    Object.keys(this.businessRegistrationForm.controls).forEach((field) => {
      const control = this.businessRegistrationForm.get(field);
      if (control && control.invalid) {
        fieldsWithErrors.push(field);
        control.markAsTouched();
      }
    });

    if (
      this.businessRegistrationForm.invalid ||
      (await this.validateCustomRules())
    ) {
      return true;
    }

    return false;
  }

  async validateCustomRules(): Promise<boolean> {
    let hasError: boolean = false;

    if (!this.mobileNoVerified()) {
      hasError = true;
      const control = this.businessRegistrationForm.get([
        BusinessInputNameConstant.MOBILE_NO,
      ]);
      control?.markAsUntouched();
      this.errorMessage.set({
        [BusinessInputNameConstant.MOBILE_NO]: {
          text: 'Please verify the Phone no.',
          type: MessageType.ERROR,
        },
      });
    }

    return hasError;
  }

  private _getButtonObjectByIndex(index: number) {
    if (index < 0 || index >= this.businessFormConfig().length) {
      return null; // Handle out-of-bound index
    }
    const section = this.businessFormConfig()[index];
    if (section && section.button) {
      return section.button;
    }
    return null; // Return null if no button is present for that index
  }

  private _updateActionButtonObject(
    index: number,
    newButtonConfig: Record<string, any>,
  ) {
    const currentConfig = this.businessFormConfig();
    if (currentConfig[index] && currentConfig[index].button) {
      currentConfig[index].button = {
        ...this._getButtonObjectByIndex(index),
        ...newButtonConfig,
      };
      this.businessFormConfig.set(currentConfig);
    }
  }

  /**
   * The function `showErrorOnLoad` iterates over error messages and updates the error state for each
   * input field.
   * @param errorMessage - The `errorMessage` parameter in the `showErrorOnLoad` function seems to be an
   * object that contains error messages for different keys. The function iterates over the keys of the
   * `errorMessage` object, marks the input associated with each key as touched using
   * `this.markInputUnTouched(key)`,
   */
  showErrorOnLoad(
    errorMessage: Record<string, { text: string; type: string }>,
  ) {
    if (errorMessage) {
      const updatedErrorMessages = { ...this.errorMessage };

      Object.keys(errorMessage).forEach((key) => {
        if (errorMessage[key].type === MessageType.ERROR) {
          this.setCustomError(key, true);
        }

        updatedErrorMessages[key] = {
          ...errorMessage[key],
        };
      });

      this.errorMessage.set(updatedErrorMessages);
    }
  }

  customErrorValidator(customError: boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // Get the current errors on the control
      const errors = { ...control.errors };

      if (customError) {
        // If customError is true, set the custom error
        errors['customError'] = true;
      } else {
        // If customError is false or null, remove the customError from the errors
        delete errors['customError'];
      }

      // If there are no other errors left, return null (indicating the control is valid)
      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  setCustomError(key: string, customError: boolean) {
    const field = this.getIndividualField(key);
    const control = this.businessRegistrationForm.get(key);

    if (!control) return;

    const validators: ValidatorFn[] = [];

    if (control?.errors?.customError) {
      if (field?.required) {
        validators.push(Validators.required);
      }
      if (field?.minlength) {
        validators.push(Validators.minLength(field?.minlength));
      }

      if (field?.pattern) {
        validators.push(Validators.pattern(field?.pattern));
      }
      if (field?.customValidationFunction) {
        const validationFn = this[field.customValidationFunction]?.();
        if (validationFn) {
          validators.push(validationFn);
        }
      }

      if (!customError) {
        if (field?.name === BusinessInputNameConstant.VERIFY_IDENTIFICATION) {
          const businessIdentificationValue: string = this.getInputFieldValue(
            BusinessInputNameConstant.BUSINESS_IDENTIFICATION,
          );
          const validation: ValidatorFn[] =
            this.setValidationForVerificationOption(
              businessIdentificationValue,
              field,
            );
          validators.push(...validation);
        }
        if (field?.name === BusinessInputNameConstant.GSTIN) {
          validators.push(this.customErrorValidator(customError));
        }
      }
    }

    // Add the custom error validator
    if (customError) {
      validators.push(this.customErrorValidator(customError));
    }

    if (!StringUtils.isEmpty(validators)) {
      control.setValidators(validators);
      control.updateValueAndValidity();
      this.markInputTouchState(key, true);
    }
  }

  isInputInvalid(inputName: string): boolean {
    const control = this.businessRegistrationForm.get(inputName);
    if (!control) return false;
    return control.invalid;
  }

  markInputTouchState(fieldName: string, isTouched: boolean) {
    const control = this.businessRegistrationForm.get(fieldName);
    control?.[isTouched ? 'markAsTouched' : 'markAsUntouched']();
  }

  goToNextStep(stepper?: MatStepper) {
    // If none of the inputs are invalid, go to the next step
    if (this.isFirstStepIsValid()) {
      this.selectedStepIndex.set(1);
      stepper ? stepper.next() : null;
    } else {
      this.selectedStepIndex.set(0);
    }
  }

  subscribeToFirstStepValidation() {
    this.businessRegistrationForm
      .get(BusinessInputNameConstant.VERIFY_IDENTIFICATION)
      ?.valueChanges.subscribe((value) => {
        this.disableSubmitButton.set(
          this.isInputInvalid(BusinessInputNameConstant.VERIFY_IDENTIFICATION),
        );

        const businessIdentificationValue: string = this.getInputFieldValue(
          BusinessInputNameConstant.BUSINESS_IDENTIFICATION,
        );

        if (
          this.documentRequired.includes(
            BusinessVerificationEnum[businessIdentificationValue],
          )
        ) {
          this.disableSubmitButton.set(
            this.isInputInvalid(BusinessInputNameConstant.COMPANY_NAME) ||
              this.isInputInvalid(BusinessInputNameConstant.BUSINESS_DOCUMENT),
          );
        }

        if (this.businessIdentificationVerified()) {
          this.resetBusinessFields(this.verificationIdentificationField);
          this.getValidatorsForOption(businessIdentificationValue);
        }
      });

    this.businessRegistrationForm
      .get(BusinessInputNameConstant.COMPANY_NAME)
      ?.valueChanges.subscribe((value) => {
        this.disableSubmitButton.set(
          this.isInputInvalid(BusinessInputNameConstant.COMPANY_NAME) ||
            this.isInputInvalid(BusinessInputNameConstant.BUSINESS_DOCUMENT),
        );
      });
    this.businessRegistrationForm
      .get(BusinessInputNameConstant.BUSINESS_DOCUMENT)
      ?.valueChanges.subscribe((value) => {
        this.disableSubmitButton.set(
          this.isInputInvalid(BusinessInputNameConstant.BUSINESS_DOCUMENT),
        );
      });

    this.businessRegistrationForm
      .get(BusinessInputNameConstant.MOBILE_NO)
      ?.valueChanges.subscribe((value) => {
        if (
          value !== this.data?.payload?.[BusinessInputNameConstant.MOBILE_NO]
        ) {
          this.mobileNoVerified.set(false);
          const field = this.getIndividualField(
            BusinessInputNameConstant.MOBILE_NO,
          );
          field.actions.verifyButton.showVerifyButton = true;
          field.isVerified = false;
          const newButton =
            this._getButtonObjectByIndex(0)?.allActions?.sendOtp;
          this._updateActionButtonObject(0, newButton);
        }
      });

    this.businessRegistrationForm.events.subscribe((e) => {
      this.isFirstStepIsValid();
    });
  }

  isFirstStepIsValid(): boolean {
    const validateInput: string[] = [
      BusinessInputNameConstant.FULL_NAME,
      BusinessInputNameConstant.EMAIL_ID,
      BusinessInputNameConstant.MOBILE_NO,
    ];
    const isInvalid: boolean = validateInput.some((inputName) => {
      if (this.isInputInvalid(inputName)) {
        return true; // Return true if any input is invalid
      }
      return false; // Return false if the input is valid
    });

    this.formDisabled.update(() => isInvalid);
    this.isLinear = isInvalid || !this.mobileNoVerified();
    if (isInvalid || !this.mobileNoVerified()) {
      return false; // if first step is invalid
    } else {
      return true; // if first step is valid
    }
  }

  triggerEntityVerificationActionOnLoad() {
    // Manually trigger the handleEvent as if the button was clicked
    this.handleEvent(
      this.stepper,
      null,
      this.showVerificationAction()?.action?.triggerExtraStandardEvents,
      null,
    );
  }

  handleInput(event: KeyboardEvent, field) {
    if (field?.isNumber) {
      const key = event.key;
      if (!/^[0-9]$/.test(key) && key !== 'Backspace' && key !== 'Enter') {
        event.preventDefault(); // Prevent the input if it's not valid
      }
    }
  }

  checkAndTransformInput(event: Event, field): void {
    this.setCustomError(field?.name, false);
    this.deleteErrorMessage(field?.name);

    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement.value;

    // Check conditions
    if (field?.isUpperCase) {
      inputElement.value = inputValue.toUpperCase();
      this.businessRegistrationForm
        .get(field?.name)
        ?.setValue(inputElement.value);
    }
  }

  setVerificationActionButton() {
    const option: BusinessVerificationEnum = this.getInputFieldValue(
      BusinessInputNameConstant.BUSINESS_IDENTIFICATION,
    );
    const verificationActionButton: Record<string, any> =
      this.verificationAction.find((item) => item[option]);
    this.showVerificationAction.set(verificationActionButton?.[option] || {});
  }

  deleteErrorMessage(fieldName: string) {
    delete this.errorMessage()?.[fieldName];
  }

  setValidationForVerificationOption(
    businessIdentificationValue: string,
    field,
  ) {
    const selectedValidation: Record<string, any> = this.validationRules.find(
      (item) => businessIdentificationValue in item,
    );

    if (StringUtils.isEmpty(selectedValidation)) return;

    const validationRules =
      selectedValidation[businessIdentificationValue] || [];

    if (!Array.isArray(validationRules)) return;

    const validators: ValidatorFn[] = [];

    validationRules.forEach((validation) => {
      if (validation && validation.rule) {
        switch (validation.rule) {
          case 'required':
            validators.push(Validators.required);
            field.validation.errorMessages.required = validation.message;
            break;
          case 'pattern':
            if (validation.value) {
              validators.push(Validators.pattern(validation.value));
              field.validation.errorMessages.pattern = validation.message;
            }
            break;
          case 'minlength':
            if (validation.value) {
              validators.push(Validators.minLength(validation.value));
              field.validation.errorMessages.minlength = validation.message;
            }
            break;
          case 'maxlength':
            if (validation.value) {
              validators.push(Validators.maxLength(validation.value));
              field.validation.errorMessages.maxlength = validation.message;
            }
            break;
          default:
            break;
        }
      }
    });

    return validators;
  }
}
